// The gate sequence from CLAUDE.md, "Gate order". Stops at the first failure so the message
// names the real obstacle.
import { isPtoEligible } from './accrual.js';
import { breakdown, lastHoliday } from './calendar.js';
import { blackoutMessage, blackoutOver } from './company-events.js';
import { conflicts } from './conflicts.js';
import { isValidISODate, mdy } from './dates.js';
import { planPtoDays } from './hours.js';
import { lockedMessage, ptoLockedThrough } from './lock.js';
import { usableSupervisor } from './permissions.js';
import { project } from './projection.js';
import { decidedByHr, kindAllowances, overLimit, requestLabel, requestRef, VOTE_HOURS } from './timeoff.js';
import { BEREAVEMENT_RELATIONSHIPS, isLive, REQUEST_TYPES, TIME_OFF_KINDS, VOTE_WINDOWS, } from './types.js';
/** Compare hours with a tolerance so the 23.8-vs-24.0 boundary is not decided by float noise. */
export const EPSILON = 1e-9;
/**
 * The later request a new one would leave short, the worst if several, and by how much the new
 * one must shrink to put it back: never more than it asks for, since a later request already
 * short is made no worse by asking for nothing (S28, 2 Nov 2026).
 */
export function laterShort(later, hours) {
    let worst = null;
    for (const l of later)
        if (l.short > 0 && (!worst || l.short > worst.short))
            worst = l;
    return worst ? { item: worst, shortBy: Math.min(worst.short, hours) } : null;
}
/** Jeremy's wording (2 Nov 2026), after "Short by …" in the refusal and in the working alone. */
export function laterShortMessage(l) {
    return `This would leave ${requestRef('PTO', l.start, l.end)} short: available then would be ${l.available.toFixed(1)} h; it asks for ${l.hours.toFixed(1)} h.`;
}
/** HR's wording (19 Oct 2026), the same on the screen and in every API refusal. */
export const NO_BALANCE_MESSAGE = 'No balance on file — ask HR.';
export function evaluateRequest(draft, ctx, opts = {}) {
    const ev = {
        ok: false, failure: null, breakdown: null, days: [], chargedHours: 0, projection: null, allowances: [], outcome: null, warnings: [],
    };
    const fail = (f) => ({ ...ev, failure: f });
    const invalid = (message) => fail({ gate: 'input', code: 'INVALID_INPUT', message });
    // Input — the shape of the request, before any rule.
    if (!REQUEST_TYPES.includes(draft.type))
        return invalid(`Unknown request type ${JSON.stringify(draft.type)}.`);
    if (!isValidISODate(draft.start) || !isValidISODate(draft.end))
        return invalid('Dates must be real YYYY-MM-DD dates.');
    if (draft.end < draft.start)
        return invalid('The last day has to fall on or after the first.');
    const ids = draft.resourceIds;
    if (draft.type === 'FIELD' ? ids.length === 0 : ids.length !== 1) {
        return invalid(draft.type === 'FIELD' ? 'Assign at least one resource to the deployment.' : `${draft.type} is for exactly one person.`);
    }
    if (new Set(ids).size !== ids.length)
        return invalid('A resource is listed more than once.');
    const people = [];
    for (const id of ids) {
        const p = ctx.people.get(id);
        if (!p)
            return invalid(`Unknown resource ${id}.`);
        if (!p.active)
            return invalid(`${p.name} is not active in Ajera and cannot be booked.`);
        people.push(p);
    }
    if (draft.perDay && draft.type !== 'PTO')
        return invalid('Partial days apply to PTO only.');
    // The kind of time off: PTO only, and a relationship on bereavement and nothing else.
    const kind = draft.timeOffKind;
    if (kind !== undefined) {
        if (!TIME_OFF_KINDS.includes(kind))
            return invalid(`Unknown kind of time off ${JSON.stringify(kind)}.`);
        if (draft.type !== 'PTO')
            return invalid('Jury duty, bereavement, volunteer, vote and unpaid time off are kinds of PTO. Only a PTO request can be one.');
    }
    const relationship = draft.bereavementRelationship;
    if (kind === 'BEREAVEMENT') {
        if (relationship === undefined || !BEREAVEMENT_RELATIONSHIPS.includes(relationship)) {
            return invalid('Choose who the bereavement is for: immediate family, extended family or someone else.');
        }
    }
    else if (relationship !== undefined) {
        return invalid('Only a bereavement request names a relationship.');
    }
    // Vote (HR, 12 Oct 2026): 2 h, fixed, on one day, at the start or the end of it, and it says which.
    const voteWindow = draft.voteWindow;
    if (kind === 'VOTE') {
        if (voteWindow === undefined || !VOTE_WINDOWS.includes(voteWindow))
            return invalid('Choose when you will vote: the start of the day or the end of it.');
        if (draft.start !== draft.end)
            return invalid('Time off to vote is for one day.');
        const asked = draft.perDay ? Object.entries(draft.perDay) : [];
        if (asked.some(([date, h]) => date !== draft.start || h !== VOTE_HOURS))
            return invalid(`Time off to vote is ${VOTE_HOURS} hours, fixed.`);
    }
    else if (voteWindow !== undefined) {
        return invalid('Only time off to vote says the start or the end of the day.');
    }
    const exclude = new Set();
    if (draft.supersedesRequestId !== undefined) {
        const old = ctx.requests.find(r => r.id === draft.supersedesRequestId);
        if (!old)
            return invalid('The request being rescheduled does not exist.');
        if (!isLive(old))
            return invalid(`The request being rescheduled is ${old.status.toLowerCase()} and cannot be rescheduled.`);
        if (!old.resourceIds.some(id => ids.includes(id)))
            return invalid('The request being rescheduled is not for the same people.');
        exclude.add(old.id);
    }
    // The lock — before every other rule, so the message names the real obstacle: no other
    // answer matters for PTO payroll has already processed. A reschedule is refused when either
    // the new dates or the request it replaces are locked, since it would cancel the old one.
    if (!opts.lockExempt) {
        const lock = ptoLockedThrough(draft, ctx.balances);
        if (lock)
            return fail({ gate: 'lock', code: 'PTO_LOCKED', message: lockedMessage(lock) });
        const old = draft.supersedesRequestId === undefined ? undefined : ctx.requests.find(r => r.id === draft.supersedesRequestId);
        const oldLock = old ? ptoLockedThrough(old, ctx.balances) : null;
        if (oldLock)
            return fail({ gate: 'lock', code: 'PTO_LOCKED', message: lockedMessage(oldLock, 'the request being rescheduled') });
    }
    const subject = draft.type === 'FIELD' ? null : people[0];
    const bd = breakdown(draft.start, draft.end, ctx.holidays);
    ev.breakdown = bd;
    const last = lastHoliday(ctx.holidays);
    if (last === null || draft.end > last) {
        ev.warnings.push({
            code: 'HOLIDAYS_NOT_LOADED',
            message: last === null
                ? 'No company holidays are loaded. Every weekday is being charged.'
                : `Company holidays are only loaded through ${mdy(last)}. Days after that are charged as working days.`,
        });
    }
    // Gate 0 — PTO eligibility. Every kind too (H4): part-time staff cannot mark jury duty or
    // bereavement, and cannot be given unpaid time off.
    if (draft.type === 'PTO' && !isPtoEligible(subject)) {
        return fail({
            gate: 'eligibility', code: 'NOT_PTO_ELIGIBLE',
            message: kind
                ? `${subject.name} is part-time. Part-time staff can't be given jury duty, bereavement, volunteer, vote or unpaid time off.`
                : `${subject.name} is part-time and does not accrue PTO. PTO requests are not available; field bookings still are.`,
        });
    }
    // Gate 1 — any working days at all. PTO only; NTD and FIELD may claim a weekend.
    if (draft.type === 'PTO' && bd.counted === 0) {
        return fail({ gate: 'workingDays', code: 'NO_WORKING_DAYS', message: 'This range is entirely weekends and holidays — there is nothing to charge.' });
    }
    // Gate 1b — a company BLACKOUT (S25, 1 Nov 2026; company-events.ts). After working days, so
    // "you cannot book them" only follows "there are days to book", and before already committed,
    // because a personal clash can be rescheduled and a blackout cannot. Every type and every kind,
    // HR and acting-as included: there is no exemption. Any overlap refuses the WHOLE request.
    // Information-only events are not read here.
    const blackout = blackoutOver(draft.start, draft.end, ctx.companyEvents);
    if (blackout) {
        return fail({ gate: 'blackout', code: 'COMPANY_BLACKOUT', message: blackoutMessage(blackout) });
    }
    if (draft.type === 'PTO') {
        // Vote is always its fixed 2 h on its one day, whatever the form sent (checked above).
        const perDay = kind === 'VOTE' ? { [draft.start]: VOTE_HOURS } : draft.perDay;
        const plan = planPtoDays(draft.start, draft.end, ctx.holidays, perDay);
        if (!plan.ok)
            return fail({ gate: 'input', code: 'INVALID_PARTIAL_DAYS', message: plan.message });
        ev.days = plan.days;
        ev.chargedHours = plan.total;
    }
    // Gate 2 — one commitment at a time, checked per person.
    const hits = [];
    const lines = [];
    for (const p of people) {
        const c = conflicts(p.id, draft.start, draft.end, ctx.requests, exclude);
        for (const r of c)
            hits.push({ resourceId: p.id, requestId: r.id });
        if (c.length) {
            lines.push(`${p.name} is already committed: ${c.map(r => `${requestRef(requestLabel(r.type, r.timeOffKind, false), r.start, r.end)} (${r.status.toLowerCase()})`).join('; ')}.`);
        }
    }
    if (hits.length) {
        return fail({ gate: 'conflict', code: 'ALREADY_COMMITTED', message: `${lines.join(' ')} Cancel that first.`, conflicts: hits });
    }
    // Gate 3 — balance, PTO only, skipped for UNL and for every kind: jury duty, bereavement,
    // volunteer, vote and unpaid do not use the balance, so there is nothing to be short of.
    if (draft.type === 'PTO' && kind) {
        ev.projection = { kind: 'NOT_CHARGED' };
    }
    else if (draft.type === 'PTO') {
        const pr = project(subject, ctx.balances.get(subject.id), draft.end, ctx.requests, exclude, { start: draft.start, hours: ev.chargedHours });
        ev.projection = pr;
        if (pr.kind === 'NO_BALANCE') {
            return fail({ gate: 'balance', code: 'NO_BALANCE_ON_FILE', message: NO_BALANCE_MESSAGE });
        }
        if (pr.kind === 'PROJECTED' && ev.chargedHours > pr.available + EPSILON) {
            const shortBy = ev.chargedHours - pr.available;
            return fail({
                gate: 'balance', code: 'INSUFFICIENT_BALANCE', shortBy,
                message: `Short by ${shortBy.toFixed(1)} h. Available on ${mdy(draft.end)} is ${pr.available.toFixed(1)} h; this asks for ${ev.chargedHours.toFixed(1)} h.`,
            });
        }
        // It fits, but would take hours a later request needs (S28, Jeremy's wording, 2 Nov 2026).
        const squeezed = pr.kind === 'PROJECTED' ? laterShort(pr.later, ev.chargedHours) : null;
        if (squeezed) {
            return fail({
                gate: 'balance', code: 'INSUFFICIENT_BALANCE', shortBy: squeezed.shortBy,
                message: `Short by ${squeezed.shortBy.toFixed(1)} h. ${laterShortMessage(squeezed.item)}`,
            });
        }
    }
    // Gate 3a — the kind's limit: bereavement per occurrence (H1), jury duty per calendar year
    // (H2), volunteer time per rolling 365 days (PR 10). Past it, refused, naming the limit and
    // what is left, until HR says otherwise (QC3). Vote's 2 h is fixed above; it has no yearly cap.
    if (draft.type === 'PTO' && kind) {
        ev.allowances = kindAllowances(subject.id, kind, relationship, ev.days, ctx.requests, exclude);
        const over = overLimit(ev.allowances);
        if (over)
            return fail({ gate: 'kindLimit', code: 'OVER_KIND_LIMIT', message: over });
    }
    // Routing — one hop to the Ajera supervisor; no walking up the chain, so no cycle.
    if (!subject)
        ev.outcome = { kind: 'FIELD_PENDING' };
    // Unpaid is HR's to create, and HR creating it is the decision.
    else if (kind === 'UNPAID')
        ev.outcome = { kind: 'ROUTE_TO_HR' };
    // Jury duty and bereavement go to HR, not the supervisor. An auto-approve person stays
    // auto-approved, and HR is told what it was marked as. Volunteer and vote fall through to the
    // supervisor, like ordinary PTO.
    else if (decidedByHr(kind) && !subject.ptoApprovalAuto)
        ev.outcome = { kind: 'ROUTE_TO_HR' };
    // NTD is approved on submit for everyone (Stevee, 9 Oct 2026): no explanation required,
    // and the supervisor, HR and HR's "also notify" list are TOLD rather than asked.
    else if (subject.ptoApprovalAuto || draft.type === 'NTD')
        ev.outcome = { kind: 'AUTO_APPROVE' };
    else {
        const approverId = usableSupervisor(subject.id, ctx.people);
        ev.outcome = { kind: 'ROUTE_TO_SUPERVISOR', approverId };
        if (!approverId) {
            ev.warnings.push({ code: 'NO_USABLE_APPROVER', message: `${subject.name} has no active supervisor other than themselves in Ajera. Only HR can decide this request.` });
        }
    }
    return { ...ev, ok: true };
}
//# sourceMappingURL=gates.js.map